import { useEffect, useState } from "react";
import { awardLoyalty, fetchDoorAccess } from "../services/reservationService";
import { animatePoints } from "../utils/animatePoints"
import { useBooking } from "../context/BookingContext";
import { useLocation } from "react-router-dom";
import { useAppLanguage } from "../hooks/useAppLanguage";
import Modal from "../components/Modal";
import Loyalty from "../components/Loyalty";
import ga_logo from "../assets/images/ga_logo.png";
import {
    canUseDigitalKey,
    getRoomAccessMessage,
    getStoredRoomAccess,
    hasNonBlockingRoomAccessPayment,
    isRoomAccessTestOverrideEnabled,
    isRoomAccessPaymentBlocking,
    normalizeRoomAccess,
    storeRoomAccess,
} from "../utils/roomAccess";
import { canUseCapability } from "../utils/journeyAccess";

const LEGACY_HOME_VISIT_STORAGE_PREFIX = "guest-journey-home-visited:";

const clearLegacyHomeVisitState = () => {
    Object.keys(localStorage)
        .filter((key) => key.startsWith(LEGACY_HOME_VISIT_STORAGE_PREFIX))
        .forEach((key) => localStorage.removeItem(key));
};

const homeTranslations = {
    de: {
        language: "Sprache",
        guestFallback: "Gast",
        welcome: "Willkommen,",
        guestServices: "Gast Services",
        digitalKey: "Digitaler Schlüssel",
        digitalKeyCopy: "Privater Zugang zu Ihrem Zimmer",
        roomAccessBlocked: "Zimmerzugang ist noch nicht verfügbar.",
        paymentRequired: "Zahlung erforderlich, bevor der Zimmerzugang aktiviert wird.",
        payBalance: "Zum Checkout",
        payBalanceTitle: "Offenen Betrag bezahlen",
        payBalanceNonBlocking: "Die Zahlung ist noch offen, Ihr Zimmerzugang bleibt verfügbar.",
        testOverrideActive: "Test-Override aktiv",
        hotelDirectory: "Hotel Directory",
        hotelDirectoryCopy: "Hotelinfos und Services ansehen",
        checkout: "Checkout",
        checkoutCopy: "Aufenthalt abschließen",
        customerSupport: "Kundenservice",
        customerSupportCopy: "Hilfe während Ihres Aufenthalts anfragen",
        callSupport: "Support anrufen",
        loyaltyError: "Beim Buchen der Loyalty-Punkte ist ein Fehler aufgetreten.",
        blockerMessages: {
            ONLINE_CHECKIN_REQUIRED: "Bitte schließen Sie zuerst den Online Check-in ab.",
            ARRIVAL_TIME_NOT_REACHED: "Wenn Sie früher Zugang zu Ihrem Zimmer wünschen, wenden Sie sich bitte an die Rezeption.",
            PAYMENT_REQUIRED: "Bitte begleichen Sie zuerst den offenen Betrag.",
            PAYMENT_STATUS_UNVERIFIED: "Der Zahlungsstatus konnte gerade nicht geprüft werden.",
            PAYMENT_STATUS_PENDING: "Der Zahlungsstatus ist noch nicht verfügbar.",
            RESERVATION_EXPIRED: "Der Zugang ist abgelaufen.",
        },
    },
    en: {
        language: "Language",
        guestFallback: "Guest",
        welcome: "Welcome,",
        guestServices: "Guest Services",
        digitalKey: "Digital Key",
        digitalKeyCopy: "Private access to your room",
        roomAccessBlocked: "Room access is not available yet.",
        paymentRequired: "Payment is required before room access can be activated.",
        payBalance: "Go to checkout",
        payBalanceTitle: "Pay outstanding balance",
        payBalanceNonBlocking: "Payment is still due, but your room access remains available.",
        testOverrideActive: "Test override active",
        hotelDirectory: "Hotel Directory",
        hotelDirectoryCopy: "Explore hotel details and amenities",
        checkout: "Checkout",
        checkoutCopy: "Complete your stay",
        customerSupport: "Customer Support",
        customerSupportCopy: "Request assistance during your stay",
        callSupport: "Call support",
        loyaltyError: "An error occurred while awarding loyalty points.",
        blockerMessages: {
            ONLINE_CHECKIN_REQUIRED: "Please complete online check-in first.",
            ARRIVAL_TIME_NOT_REACHED: "If you would like earlier access to your room, please visit reception.",
            PAYMENT_REQUIRED: "Please settle the open balance first.",
            PAYMENT_STATUS_UNVERIFIED: "Payment status could not be verified right now.",
            PAYMENT_STATUS_PENDING: "Payment status is not available yet.",
            RESERVATION_EXPIRED: "Access has expired.",
        },
    },
};

const capitaliseName = (value) =>
    String(value || "")
        .trim()
        .split(/\s+/)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join(" ");

const getLocalizedRoomAccessMessage = (roomAccess, t, fallback) => {
    const normalizedRoomAccess = normalizeRoomAccess(roomAccess);
    const translated = (normalizedRoomAccess.blocker_codes || [])
        .map((code) => t.blockerMessages?.[String(code || "").toUpperCase()])
        .filter(Boolean);

    if (translated.length > 0) {
        return translated.join(" ");
    }

    return getRoomAccessMessage(normalizedRoomAccess, fallback);
};

const Home = () => {
    const booking = useBooking();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const loyalty = queryParams.get("loyalty");
    const [data, setData] = useState(null);
    const [success, setSuccess] = useState(null);
    const [animatedPoints, setAnimatedPoints] = useState(0);
    const [showModal, setShowModal] = useState(false);
    const [language, setLanguage] = useAppLanguage();
    const t = homeTranslations[language] || homeTranslations.de;
    const roomAccess = normalizeRoomAccess(booking["room-access"] || getStoredRoomAccess());
    const digitalKeyAvailable = canUseDigitalKey(roomAccess);
    const testOverrideActive = isRoomAccessTestOverrideEnabled(roomAccess);
    const roomAccessPaymentBlocking = isRoomAccessPaymentBlocking(roomAccess);
    const nonBlockingPaymentRequired = hasNonBlockingRoomAccessPayment(roomAccess);
    const canUseCheckout = canUseCapability("checkout");
    const canUsePayment = canUseCapability("payment");
    const canUseOwnerPayment = canUseCheckout || canUsePayment;
    const roomAccessMessage = getLocalizedRoomAccessMessage(
        roomAccess,
        t,
        roomAccessPaymentBlocking ? t.paymentRequired : t.roomAccessBlocked
    );
    const name = capitaliseName(localStorage.getItem("guest-firstname"))
    const lastName = capitaliseName(localStorage.getItem("guest-lastname"))
    const guestName = [name, lastName].filter(Boolean).join(" ") || t.guestFallback;
    const directoryPropertyCode = String(localStorage.getItem("apaleo-property-id") || "").trim().toUpperCase();
    const directoryHref = directoryPropertyCode
        ? `/hotel-directory/${encodeURIComponent(directoryPropertyCode)}`
        : "/hotel-directory";
    const handleLanguageChange = (event) => {
        setLanguage(event.target.value);
    };

    useEffect(() => {
        let isMounted = true;
        let requestInFlight = false;

        clearLegacyHomeVisitState();

        const refreshDoorAccess = () => {
            if (requestInFlight) return;
            requestInFlight = true;

            fetchDoorAccess()
                .then((accessState) => {
                    if (!isMounted) return;

                    if (accessState?.roomAccess) {
                        storeRoomAccess(accessState.roomAccess);
                        window.dispatchEvent(new Event("booking-update"));
                    }

                })
                .catch((error) => {
                    const roomAccessFromError = error?.response?.data?.roomAccess || error?.response?.data?.room_access;
                    if (!isMounted || !roomAccessFromError) return;

                    storeRoomAccess(roomAccessFromError);
                    window.dispatchEvent(new Event("booking-update"));
                })
                .finally(() => {
                    requestInFlight = false;
                });
        };

        const refreshOnResume = () => refreshDoorAccess();
        const refreshWhenVisible = () => {
            if (document.visibilityState === "visible") refreshDoorAccess();
        };

        refreshDoorAccess();
        window.addEventListener("focus", refreshOnResume);
        window.addEventListener("pageshow", refreshOnResume);
        document.addEventListener("visibilitychange", refreshWhenVisible);

        return () => {
            isMounted = false;
            window.removeEventListener("focus", refreshOnResume);
            window.removeEventListener("pageshow", refreshOnResume);
            document.removeEventListener("visibilitychange", refreshWhenVisible);
        };
    }, []);

    useEffect(() => {
        if (loyalty === "true") {
            const loyaltyReservationCode = localStorage.getItem("reservation-code");
            awardLoyalty(loyaltyReservationCode)
                .then((e) => {
                    console.log(e, "Loyalty points awarded successfully.");
                    setData(e.booker);
                    setSuccess(e.success);
                    // setShowModal(e.success);
                    const oldPoints = booking.booker?.points || 0;
                    const newPoints = e.booker?.points || 0;
                    if (e.success) {
                        animatePoints(oldPoints, newPoints, setAnimatedPoints);
                        localStorage.setItem("booker", JSON.stringify(e.booker));
                    }
                })
                .catch((error) => {
                    console.error("Error awarding loyalty points:", error);
                    alert(error.response?.data?.error || t.loyaltyError);
                });
        }
    }, [loyalty]);

    return (
        <>
            <div className="app home-app">
                <header className="home-header">
                    <div className="home-header-top">
                        <img src={ga_logo} alt="guestified grand airport hotel logo" className="home-logo" />
                        <label className="home-language-switcher">
                            <span className="visually-hidden">{t.language}</span>
                            <select value={language} onChange={handleLanguageChange}>
                                <option value="de">DE</option>
                                <option value="en">EN</option>
                            </select>
                        </label>
                    </div>
                    {/* <p className="home-eyebrow">guestified Grand Airport</p> */}
                    <h1>{t.welcome}</h1>
                    <h1>{guestName}</h1>
                    {/* <p className="home-intro">Your personal stay services are ready.</p> */}
                </header>

                <main className="home-menu-wrap">
                    <div className="home-section-heading">
                        <span>{t.guestServices}</span>
                    </div>

                    <div className="menu">
                        {digitalKeyAvailable ? (
                            <a href="/digital-key" className="menu-item">
                                <div className="home-icon digital-key-icon" aria-hidden="true"></div>
                                <div>
                                    <h2>{t.digitalKey}</h2>
                                    <p>{t.digitalKeyCopy}</p>
                                    {testOverrideActive && (
                                        <span className="menu-test-badge">{t.testOverrideActive}</span>
                                    )}
                                </div>
                                <span className="menu-arrow" aria-hidden="true"></span>
                            </a>
                        ) : (
                            <div className="menu-item menu-item-disabled" aria-disabled="true">
                                <div className="home-icon digital-key-icon" aria-hidden="true"></div>
                                <div>
                                    <h2>{t.digitalKey}</h2>
                                    <p>{roomAccessMessage}</p>
                                    {roomAccessPaymentBlocking && canUseOwnerPayment && (
                                        <a href="/checkout" className="menu-inline-button">
                                            {t.payBalance}
                                        </a>
                                    )}
                                </div>
                            </div>
                        )}

                        {nonBlockingPaymentRequired && canUseOwnerPayment && (
                            <a href="/checkout" className="menu-item">
                                <div className="home-icon checkout-icon" aria-hidden="true"></div>
                                <div>
                                    <h2>{t.payBalanceTitle}</h2>
                                    <p>{t.payBalanceNonBlocking}</p>
                                </div>
                                <span className="menu-arrow" aria-hidden="true"></span>
                            </a>
                        )}

                        {/* Old external directory redirect used checkHotelsWebsiteUrl(booking["hotel-id"]). */}
                        <a href={directoryHref} className="menu-item">
                            <div className="home-icon directory-icon" aria-hidden="true"></div>
                            <div>
                                <h2>{t.hotelDirectory}</h2>
                                <p>{t.hotelDirectoryCopy}</p>
                            </div>
                            <span className="menu-arrow" aria-hidden="true"></span>
                        </a>

                        {/* <a href="/loyalty-program" className="menu-item">
                            <div className="home-icon loyalty-icon" aria-hidden="true"></div>
                            <div>
                                <h2>Loyalty Program</h2>
                                <p>Review points and member benefits</p>
                            </div>
                            <span className="menu-arrow" aria-hidden="true"></span>
                        </a> */}

                        {canUseCheckout && (
                            <a href="/checkout" className="menu-item">
                                <div className="home-icon checkout-icon" aria-hidden="true"></div>
                                <div>
                                    <h2>{t.checkout}</h2>
                                    <p>{t.checkoutCopy}</p>
                                </div>
                                <span className="menu-arrow" aria-hidden="true"></span>
                            </a>
                        )}

                        {/* <a href="/room-service" className="menu-item">
                            <div className="home-icon room-service-icon" aria-hidden="true"></div>
                            <div>
                                <h2>Room Service</h2>
                                <p>Order food and beverages</p>
                            </div>
                            <span className="menu-arrow" aria-hidden="true"></span>
                        </a> */}

                        {/* <a href="/customer-support" className="menu-item">
                            <div className="home-icon support-icon" aria-hidden="true"></div>
                            <div>
                                <h2>{t.customerSupport}</h2>
                                <p>{t.customerSupportCopy}</p>
                            </div>
                            <span className="menu-arrow" aria-hidden="true"></span>
                        </a> */}

                        {/* <a href="/feedback" className="menu-item">
                            <div className="home-icon feedback-icon" aria-hidden="true"></div>
                            <div>
                                <h2>Feedback</h2>
                                <p>Share your stay experience</p>
                            </div>
                            <span className="menu-arrow" aria-hidden="true"></span>
                        </a> */}
                    </div>
                </main>
                <a className="floating-phone" href="tel:022892993232" aria-label={t.callSupport}>
                    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                        <path d="M6.6 10.8c1.6 3.1 3.5 5 6.6 6.6l2.2-2.2c.3-.3.8-.4 1.2-.2 1.3.4 2.6.7 4 .7.7 0 1.2.5 1.2 1.2v3.5c0 .7-.5 1.2-1.2 1.2C10.5 21.6 2.4 13.5 2.4 3.4c0-.7.5-1.2 1.2-1.2h3.5c.7 0 1.2.5 1.2 1.2 0 1.4.2 2.7.7 4 .1.4 0 .8-.3 1.2l-2.1 2.2z" />
                    </svg>
                </a>
            </div>
            <Modal isOpen={showModal} onClose={() => setShowModal(false)} >
                {success && data && <Loyalty data={data} animatedPoints={animatedPoints} booking={booking} />}
            </Modal>
        </>
    )
}

export default Home
